"use client"

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  ReactNode,
} from "react"

import { PLAYLIST as BASE_PLAYLIST } from "@/data"

import { useMusicPlayer } from "./useMusicPlayer"
import { useMusicRadio } from "./useMusicRadio"

import { useMediaSession } from "./music/useMediaSession"

import { db } from "@/app/pages/favoritosPage/db/db"
import {
  useNativePlayer,
  NativePlayerEvent,
} from "@/hooks/useNativePlayer"

/* =========================================================
   TYPES
========================================================= */

export type Song = {
  url?: string
  link?: string
  blob?: Blob | null
  songName?: string
  songArtist?: string
  songimg?: string
  artistLink?: string

  [key: string]: any
}

export type Playlist = {
  playlistData?: Song[]
  title?: string
  link?: string
  id?: string
  type?: string

  [key: string]: any
}

export type CurrentTrackData = {
  type?: "song" | "radio" | string

  track?: string
  trackName?: string
  trackArtist?: string
  trackImg?: string

  playlistLink?: string
  playlistTitle?: string

  trackKey?: any

  trackTimeElapsed?: number
  trackDuration?: number

  radioId?: string
  radioName?: string
  radioColor?: string

  startTime?: string
  endTime?: string

  [key: string]: any
}

/* =========================================================
   HELPERS
========================================================= */

export const getPlaylistData = (
  playlist: Playlist | null | undefined,
): Song[] => {
  if (!playlist?.playlistData) {
    return []
  }

  return playlist.playlistData
}

/* =========================================================
   CONTEXT TYPE
========================================================= */

export type MusicContextType = {
  /* =========================
     AUDIO
  ========================= */

  audioRef: React.RefObject<HTMLAudioElement | null>

  isPlaying: boolean

  setIsPlaying: React.Dispatch<
    React.SetStateAction<boolean>
  >

  isLoading: boolean

  isMusicLoading: boolean

  isBuffering: boolean

  /* =========================
     CURRENT SONG
  ========================= */

  currentSong: Song | null

  currentPlaylist: Playlist | null

  currentSongIndex: number

  currentTrackData: CurrentTrackData | null

  /* =========================
     PLAYLIST
  ========================= */

  playlist: Playlist[]

  /* =========================
     CONTROLS
  ========================= */

  playSong: (
    song: Song,
    index?: number,
    playlist?: Playlist,
  ) => void

  previousSong: () => void

  nextSong: () => void

  togglePlayPause: () => void

  seek: (time: number) => void

  /* =========================
     SHUFFLE
  ========================= */

  isShuffle: boolean

  toggleShuffle: () => void

  /* =========================
     RADIO
  ========================= */

  activeRadio: string | null

  isRadioPlaying: boolean

  isRadioLoading: boolean

  playRadioStream: (
    id: string,
  ) => Promise<void>

  stopRadioStream: () => void
}

/* =========================================================
   CONTEXT
========================================================= */

const MusicContext =
  createContext<MusicContextType | undefined>(
    undefined,
  )

/* =========================================================
   PROVIDER
========================================================= */

export const MusicProvider = ({
  children,
}: {
  children: ReactNode
}) => {

/* =======================================================
   NATIVE APP
======================================================= */
const isNativeApp =
  typeof window !== "undefined" &&
  !!window.webkit?.messageHandlers?.mixtape

const sendNativePlayerMessage = useCallback(
  (message: Record<string, any>) => {
    if (!isNativeApp) {
      return
    }

    window.webkit
      ?.messageHandlers
      ?.mixtape
      ?.postMessage(message)
  },
  [isNativeApp],
)


/* React.useEffect(() => {
  const handleNativeFavorite = async (
    event: Event
  ) => {
    const customEvent =
      event as CustomEvent<{
        url: string
        title: string
        artist: string
        image: string
      }>

    const data = customEvent.detail

    if (!data?.url) {
      return
    }

    try {
      const existing =
        await db.favorites.get(data.url)

      if (existing) {

        await db.favorites.delete(data.url)

        window.webkit?.messageHandlers?.mixtape?.postMessage({
          action: "favorite_state",
          url: data.url,
          isFavorite: false,
        })

        return
      }

      await db.favorites.put({
        url: data.url,

        name:
          data.title ||
          "Mixtape",

        artist:
          data.artist ||
          "",

        songimg:
          data.image ||
          "",

        savedAt:
          new Date(),
      })

      window.webkit?.messageHandlers?.mixtape?.postMessage({
        action: "favorite_state",
        url: data.url,
        isFavorite: true,
      })

    } catch (error) {

      console.error(
        "Error gestionando favorito nativo:",
        error
      )
    }
  }

  window.addEventListener(
    "mixtape:native-favorite",
    handleNativeFavorite
  )

  return () => {
    window.removeEventListener(
      "mixtape:native-favorite",
      handleNativeFavorite
    )
  }
}, []) */

React.useEffect(() => {

  // =========================================================
  // NATIVE FAVORITE EVENTS
  // =========================================================

  const handleNativePlayerEvent = async (
    event: Event
  ) => {

    const customEvent =
      event as CustomEvent<{
        event?: string
        action?: string
        url?: string
        name?: string
        title?: string
        artist?: string
        songimg?: string
        image?: string
        trackTime?: number | string
        isFavorite?: boolean
      }>

    const data =
      customEvent.detail

    if (!data) {
      return
    }

    // =======================================================
    // FAVORITO → TOGGLE
    // =======================================================

    if (
      data.event === "favoriteToggle" ||
      data.action === "toggle_favorite"
    ) {

      if (!data.url) {
        console.warn(
          "⚠️ Favorito nativo sin URL"
        )

        return
      }

      try {

        const existing =
          await db.favorites.get(
            data.url
          )

        // ---------------------------------------------------
        // YA EXISTE → ELIMINAR
        // ---------------------------------------------------

        if (existing) {

          await db.favorites.delete(
            data.url
          )

          console.log(
            "💔 Favorito eliminado:",
            data.name
          )

          sendNativePlayerMessage({
            action: "favorite_state",
            url: data.url,
            isFavorite: false,
          })

          return
        }

        // ---------------------------------------------------
        // NO EXISTE → GUARDAR
        // ---------------------------------------------------

        await db.favorites.put({

          url:
            data.url,

          name:
            data.name ||
            data.title ||
            "Mixtape",

          artist:
            data.artist ||
            "",

          songimg:
            data.songimg ||
            data.image ||
            "",

          trackTime:
            String(
              data.trackTime ??
              ""
            ),

          savedAt:
            new Date(),

        })

        console.log(
          "❤️ Favorito guardado:",
          data.name ||
          data.title
        )

        sendNativePlayerMessage({
          action: "favorite_state",
          url: data.url,
          isFavorite: true,
        })

      } catch (error) {

        console.error(
          "❌ Error gestionando favorito nativo:",
          error
        )
      }

      return
    }


    // =======================================================
    // SOLICITUD DE ESTADO DEL FAVORITO
    // =======================================================

    if (
      data.event ===
        "requestFavoriteState" ||
      data.event ===
        "request_favorite_state" ||
      data.action ===
        "get_favorite_state"
    ) {

      if (!data.url) {
        return
      }

      try {

        const existing =
          await db.favorites.get(
            data.url
          )

        sendNativePlayerMessage({
          action: "favorite_state",
          url: data.url,
          isFavorite:
            !!existing,
        })

        console.log(
          "❤️ Estado favorito enviado:",
          !!existing
        )

      } catch (error) {

        console.error(
          "❌ Error consultando favorito:",
          error
        )
      }

      return
    }

  }


  // =========================================================
  // LISTENER
  // =========================================================

  window.addEventListener(
    "mixtape:native-player",
    handleNativePlayerEvent
  )


  // =========================================================
  // CLEANUP
  // =========================================================

  return () => {

    window.removeEventListener(
      "mixtape:native-player",
      handleNativePlayerEvent
    )

  }

}, [
  sendNativePlayerMessage,
])





  /* =======================================================
     PLAYLIST
  ======================================================= */
  const [playlist] = useState<Playlist[]>(
    BASE_PLAYLIST as Playlist[],
  )

  /* =======================================================
     SHUFFLE
  ======================================================= */

  const [isShuffle, setIsShuffle] =
    useState(false)

  const toggleShuffle = useCallback(() => {
    setIsShuffle((previous) => !previous)
  }, [])

  /* =======================================================
     CURRENT TRACK DATA
  ======================================================= */

  const [currentTrackData, setCurrentTrackData] =
    useState<CurrentTrackData | null>(null)

  const currentTrackDataRef =
    useRef<CurrentTrackData | null>(null)

  const updateCurrentTrackData = useCallback(
    (
      data: CurrentTrackData | null,
    ) => {
      currentTrackDataRef.current = data

      setCurrentTrackData(data)
    },
    [],
  )

  /* =======================================================
     CURRENT SONG
  ======================================================= */

  const [currentSong, setCurrentSong] =
    useState<Song | null>(null)

  const [nativeIsPlaying, setNativeIsPlaying] =
  useState(false)

  const [currentPlaylist, setCurrentPlaylist] =
    useState<Playlist | null>(null)

  const [currentSongIndex, setCurrentSongIndex] =
    useState(0)

  /* =======================================================
     MUSIC PLAYER
  ======================================================= */

  const player = useMusicPlayer({
    playlist,

    currentSong,
    setCurrentSong,

    currentPlaylist,
    setCurrentPlaylist,

    currentSongIndex,
    setCurrentSongIndex,

    currentTrackData,
    currentTrackDataRef,

    updateCurrentTrackData,

    isShuffle,
  })

  
/* =======================================================
   RADIO
======================================================= */

const radio = useMusicRadio({
  audioRef: player.audioRef,

  currentTrackDataRef,

  updateCurrentTrackData,

  setIsPlaying:
    player.setIsPlaying,

  setIsMusicLoading:
    player.setIsMusicLoading,

  insightsPlayingRef:
    player.insightsPlayingRef,

  insightsTrackRef:
    player.insightsTrackRef,

  startTrack:
    player.startTrack,

  pause:
    player.pause,

  resume:
    player.resume,

  stop:
    player.stop,

  radioPlayRequestId:
    player.radioPlayRequestId,

  radioHasStartedRef:
    player.radioHasStartedRef,

  wantsToPlayRef:
    player.wantsToPlayRef,
})



  /* =======================================================
     NATIVE PLAYER EVENTS
  ======================================================= */
    const handleNativePlayer = useCallback(
    (event: NativePlayerEvent) => {
      switch (event.event) {
        case "state": {
          const playing = event.state === "playing"
                
          setNativeIsPlaying(playing)
          player.setIsPlaying(playing)
          player.setIsMusicLoading(false)
                
          break
        }

        case "progress": {
          const currentTime =
            Number.isFinite(
              event.current,
            )
              ? event.current
              : 0
          
          const duration =
            Number.isFinite(
              event.duration,
            )
              ? event.duration
              : 0
          
          if (
            currentTrackDataRef.current
          ) {
            updateCurrentTrackData({
              ...currentTrackDataRef.current,
            
              trackTimeElapsed:
                currentTime,
            
              trackDuration:
                duration,
            })
          }
        
          break
        }
      
        /* case "progress": {
          const currentTime =
            Number.isFinite(event.current)
              ? event.current
              : 0
                
          const duration =
            Number.isFinite(event.duration)
              ? event.duration
              : 0
                
          if (currentTrackDataRef.current) {
            updateCurrentTrackData({
              ...currentTrackDataRef.current,
            
              trackTimeElapsed:
                currentTime,
            
              trackDuration:
                duration,
            })
          }
        
          break
        } */
      
        case "ended": {
          setNativeIsPlaying(false)

          player.setIsPlaying(false)

          player.setIsMusicLoading(false)

          /*
           * iOS NativePlayer terminó una canción.
           *
           * No hacemos nextSong() directamente aquí porque
           * nextSong está declarado más abajo.
           *
           * Lanzamos un evento para que el listener lo gestione.
           */
          window.dispatchEvent(
            new CustomEvent("mixtape:native-ended")
          )
        
          break
        }
      
        case "error": {
          console.error(
            "❌ Native player error:",
            event.message,
          )
        
          setNativeIsPlaying(false)
        
          player.setIsPlaying(false)
        
          player.setIsMusicLoading(false)
        
          break
        }

        case "next":
          window.dispatchEvent(
            new CustomEvent("mixtape:native-next")
          )
          break
        
        case "previous":
          window.dispatchEvent(
            new CustomEvent("mixtape:native-previous")
          )
          break

      }
    },
    [
      player.setIsPlaying,
      player.setIsMusicLoading,
      updateCurrentTrackData,
    ],
  )

  useNativePlayer(handleNativePlayer)


    /* =======================================================
     PLAY SONG
  ======================================================= */

  const playSong = useCallback(
    (
      song: Song,
      index?: number,
      playlistData?: Playlist,
    ) => {

      /*
       * iOS → NativePlayer
       */
      if (isNativeApp) {
        if (!song.url && !song.link) {
          console.error(
            "NativePlayer: song has no URL",
          )
          return
        }
      
        const src = song.url ?? song.link
      
        const songIndex =
          typeof index === "number"
            ? index
            : 0
      
        /*
         * React state
         */
        setCurrentSong(song)
      
        if (playlistData) {
          setCurrentPlaylist(playlistData)
        }
      
        setCurrentSongIndex(songIndex)
      
        /*
         * IMPORTANTE:
         * Mantener currentTrackData sincronizado
         * incluso cuando el reproductor real es Swift.
         */
        updateCurrentTrackData({
          type: "song",
        
          track: src,
        
          trackName:
            song.songName,
        
          trackArtist:
            song.songArtist,
        
          trackImg:
            song.songimg,
        
          playlistLink:
            playlistData?.link,
        
          playlistTitle:
            playlistData?.title,
        
          trackKey: [
            playlistData?.id ?? null,
            songIndex,
          ],
        
          trackTimeElapsed: 0,
        
          _ts: Date.now(),
        })

      
        /*
         * Swift
         */
        player.setIsMusicLoading(true)
        player.setIsPlaying(false)
        
        window.webkit
          ?.messageHandlers
          ?.mixtape
          ?.postMessage({
            action: "play",
          
            url: src,
          
            title:
              song.songName ??
              "Mixtape",
          
            artist:
              song.songArtist ??
              "Unknown Artist",
          
            album:
              playlistData?.title ??
              "Mixtape",
          
            image:
              song.songimg ?? "",
          })
        
        return
      }

      /*
       * Web → HTMLAudioElement
       */
      player.playSong(
        song,
        index,
        playlistData,
      )
    },
    [
      isNativeApp,
      player.playSong,
    ],
  )


  /* =======================================================
     PREVIOUS SONG
  ======================================================= */

  const previousSong = useCallback(() => {
    if (!currentPlaylist) {
      return
    }

    const list =
      getPlaylistData(currentPlaylist)

    if (!list.length) {
      return
    }

    const index =
      currentSongIndex <= 0
        ? list.length - 1
        : currentSongIndex - 1

    playSong(
    list[index],
    index,
    currentPlaylist,
  )
    }, [
    currentPlaylist,
    currentSongIndex,
    playSong,
  ])

  /* =======================================================
     NEXT SONG
  ======================================================= */

  const nextSong = useCallback(() => {
    if (!currentPlaylist) {
      return
    }

    const list =
      getPlaylistData(currentPlaylist)

    if (!list.length) {
      return
    }

    let nextIndex: number

    if (
      isShuffle &&
      list.length > 1
    ) {
      do {
        nextIndex =
          Math.floor(
            Math.random() *
              list.length,
          )
      } while (
        nextIndex ===
        currentSongIndex
      )
    } else {
      nextIndex =
        (currentSongIndex + 1) %
        list.length
    }

    /*
     * Siguiente SIEMPRE inicia
     * reproducción.
     */
    playSong(
    list[nextIndex],
    nextIndex,
    currentPlaylist,
  )
    }, [
    currentPlaylist,
    currentSongIndex,
    isShuffle,
    playSong,
  ])


    /* React.useEffect(() => {
    const next = () => nextSong()
    const previous = () => previousSong()

    window.addEventListener("mixtape:native-next", next)
    window.addEventListener("mixtape:native-previous", previous)

    return () => {
      window.removeEventListener("mixtape:native-next", next)
      window.removeEventListener("mixtape:native-previous", previous)
    }
  }, [nextSong, previousSong]) */

  React.useEffect(() => {
    const next = () => {
      nextSong()
    }
  
    const previous = () => {
      previousSong()
    }
  
    const ended = () => {
      /*
       * RADIO NO entra aquí.
       *
       * Una radio no debe saltar a otra canción
       * mediante la playlist.
       */
      if (
        currentTrackDataRef.current?.type === "radio"
      ) {
        return
      }
    
      /*
       * Música normal:
       * cuando termina una pista, reproducimos
       * automáticamente la siguiente de su playlist.
       */
      nextSong()
    }
  
    window.addEventListener(
      "mixtape:native-next",
      next,
    )
  
    window.addEventListener(
      "mixtape:native-previous",
      previous,
    )
  
    window.addEventListener(
      "mixtape:native-ended",
      ended,
    )
  
    return () => {
      window.removeEventListener(
        "mixtape:native-next",
        next,
      )
    
      window.removeEventListener(
        "mixtape:native-previous",
        previous,
      )
    
      window.removeEventListener(
        "mixtape:native-ended",
        ended,
      )
    }
  }, [
    nextSong,
    previousSong,
  ])



useMediaSession({
  isRadio:
    currentTrackData?.type === "radio",

  isNativeApp,

  audioRef:
    player.audioRef,

  currentTrackData,

  playRadioStream:
    radio.playRadioStream,

  stopRadioStream:
    radio.stopRadioStream,

  activeRadio:
    radio.activeRadio,

  resumeAudio:
    player.resumeAudio,

  previousSong,

  nextSong,

  wantsToPlayRef:
    player.wantsToPlayRef,

  setIsMusicLoading:
    player.setIsMusicLoading,

  setIsPlaying:
    player.setIsPlaying,
})


  /* =======================================================
     TOGGLE PLAY / PAUSE
  ======================================================= */
    const togglePlayPause = useCallback(() => {

    const isRadio =
      currentTrackDataRef.current?.type === "radio"

    /*
     * ============================================
     * NATIVE iOS
     * ============================================
     */
    if (isNativeApp) {

      if (isRadio) {

        if (radio.isRadioPlaying) {
          radio.stopRadioStream()
        } else if (radio.activeRadio) {
          void radio.playRadioStream(
            radio.activeRadio,
          )
        }

        return
      }

      /*
       * No hay canción cargada.
       *
       * MUY IMPORTANTE:
       * no mandar "resume" al arrancar la app.
       */
      if (!currentSong) {
        console.log(
          "[Native] Play ignorado: no hay canción cargada",
        )

        return
      }

      sendNativePlayerMessage({
        action:
          nativeIsPlaying
            ? "pause"
            : "resume",
      })

      return
    }

    /*
     * ============================================
     * WEB
     * ============================================
     */

    if (isRadio) {

      if (player.isPlaying) {
        radio.stopRadioStream()
        return
      }

      if (radio.activeRadio) {
        void radio.playRadioStream(
          radio.activeRadio,
        )
      }

      return
    }

    const audio =
      player.audioRef.current

    if (!audio) {
      return
    }

    if (audio.paused) {
      void player.resumeAudio()
      return
    }

    player.wantsToPlayRef.current =
      false

    audio.pause()

    player.setIsMusicLoading(false)
    player.setIsPlaying(false)

  }, [
    isNativeApp,
    nativeIsPlaying,
    currentSong,
    sendNativePlayerMessage,
    radio.activeRadio,
    radio.isRadioPlaying,
    radio.playRadioStream,
    radio.stopRadioStream,
    player.audioRef,
    player.isPlaying,
    player.resumeAudio,
    player.setIsMusicLoading,
    player.setIsPlaying,
    player.wantsToPlayRef,
  ])




    /* =======================================================
     MEDIA SESSION
  ======================================================= */

  /*
   * Media Session se gestiona mediante
   * useMediaSession.
   *
   * Música:
   *   Previous / Play-Pause / Next
   *
   * Radio:
   *   Play-Pause
   */


  /* =======================================================
     RETURN CONTEXT
  ======================================================= */

  return (
    <MusicContext.Provider
      value={{
        /* =========================
           AUDIO
        ========================= */

        audioRef:
          player.audioRef,

        isPlaying:
          player.isPlaying,

        setIsPlaying:
          player.setIsPlaying,

        isLoading:
          player.isLoading,

        isMusicLoading:
          player.isMusicLoading,

        isBuffering:
          player.isBuffering,

        /* =========================
           CURRENT SONG
        ========================= */

        currentSong,

        currentPlaylist,

        currentSongIndex,

        currentTrackData,

        /* =========================
           PLAYLIST
        ========================= */

        playlist,

        /* =========================
           CONTROLS
        ========================= */

        playSong:
          playSong,
          //player.playSong,

        previousSong,

        nextSong,

        togglePlayPause,

        seek:
          player.seek,

        /* =========================
           SHUFFLE
        ========================= */

        isShuffle,

        toggleShuffle,

        /* =========================
           RADIO
        ========================= */

        activeRadio:
          radio.activeRadio,

        isRadioPlaying:
          radio.isRadioPlaying,

        isRadioLoading:
          radio.isRadioLoading,

        playRadioStream:
          radio.playRadioStream,

        stopRadioStream:
          radio.stopRadioStream,
      }}
    >
      {children}
    </MusicContext.Provider>
  )
}

/* =========================================================
   HOOK
========================================================= */

export const useMusicContext =
  () => {
    const context =
      useContext(MusicContext)

    if (!context) {
      throw new Error(
        "useMusicContext must be used within MusicProvider",
      )
    }

    return context
  }